import { hasAnyRole, UserRole } from '@myapp/contract'
import { useAuthStore } from '../../features/auth'

/**
 * Who may move something that is already finished.
 *
 * The single frontend copy of the API's rule — `UpdateTaskUseCase.COMPLETED_RESCHEDULE_ROLES` and
 * `UpdateAppointmentUseCase.assertCanEditCompleted`, which agree. Import it rather than restating
 * the list: `dragPolicy` used to carry its own narrower copy and the two silently disagreed.
 */
export const COMPLETED_RESCHEDULE_ROLES: UserRole[] = ['MANAGER', 'DIRECTOR', 'SUPERUSER']

export function useUserRoles(): UserRole[] {
  return useAuthStore((s) => s.user?.roles ?? [])
}

export function useCanRescheduleCompleted(): boolean {
  const roles = useUserRoles()
  return hasAnyRole(roles, COMPLETED_RESCHEDULE_ROLES)
}

/**
 * Who may delete a Termin or an Aufgabe — completed and started ones included.
 *
 * The single frontend copy of the `@Roles(MANAGER, DIRECTOR, SUPERUSER)` on `DELETE /appointments/:id`
 * and `DELETE /tasks/:id`. The Aufgaben page is open to everyone signed in, so without this a worker
 * would see a button that can only answer 403.
 *
 * Absent roles (no user loaded yet) fall on the restricted side on purpose.
 */
export const DELETE_WORK_ITEM_ROLES: UserRole[] = ['MANAGER', 'DIRECTOR', 'SUPERUSER']

export function useCanDeleteWorkItems(): boolean {
  return hasAnyRole(useUserRoles(), DELETE_WORK_ITEM_ROLES)
}

/**
 * Who may plan and edit an absence of **any** category.
 *
 * Everyone else is limited to Privatzeit: they get a direct Privatzeit button instead of the
 * category menu, `/zeitmanagement/new` only opens for `PERSONAL_LEAVE`, and
 * `/zeitmanagement/:id/edit` is read-only. One list, three screens — restating it per page is how
 * the three drift apart.
 *
 * Absent roles (no user loaded yet) fall on the restricted side on purpose.
 */
export const ABSENCE_PLANNER_ROLES: UserRole[] = ['DIRECTOR', 'SUPERUSER']

/**
 * Who may arrange the Handwerker in the default calendar (order and hiding). The API route is
 * gated the same way; MANAGER sees the result but cannot change it.
 */
export const CALENDAR_ORDER_ROLES: UserRole[] = ['DIRECTOR', 'SUPERUSER']

export function useCanArrangeTechnicians(): boolean {
  return hasAnyRole(useUserRoles(), CALENDAR_ORDER_ROLES)
}

export function useCanPlanAllAbsences(): boolean {
  return hasAnyRole(useUserRoles(), ABSENCE_PLANNER_ROLES)
}
